/*
 * Portions of this code are from Novel (https://github.com/steven-tey/novel)
 * Copyright (c) 2023 Steven Tey
 * Licensed under the Apache License, Version 2.0
 */

import { EditorCommandOut } from '@/components/tiptap-editor/editor-command'
import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  type ReferenceElement,
} from '@floating-ui/dom'
import type { Editor, Range } from '@tiptap/core'
import { Extension } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import type { ReactNode } from 'react'

const Command = Extension.create({
  name: 'slash-command',
  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }) => {
          props.command({ editor, range })
        },
      } as SuggestionOptions,
    }
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})

const renderItems = () => {
  let component: ReactRenderer | null = null
  let popup: HTMLDivElement | null = null
  let cleanup: (() => void) | null = null
  let container: HTMLElement | null = null

  return {
    onStart: (props: {
      editor: Editor
      clientRect: (() => DOMRect) | null
    }) => {
      component = new ReactRenderer(EditorCommandOut, {
        props,
        editor: props.editor,
      })

      const { selection } = props.editor.state

      const parentNode = selection.$from.node(selection.$from.depth)
      const blockType = parentNode.type.name

      if (blockType === 'codeBlock') {
        return false
      }

      // clientRect is a function, not a direct value - call it to get the DOMRect
      const clientRect =
        typeof props.clientRect === 'function'
          ? props.clientRect()
          : props.clientRect

      if (!clientRect) {
        return false
      }

      // Find the EditorContent container - the div wrapper that contains both editor and EditorCommand
      const editorDom = props.editor.view.dom
      // EditorContent creates a div, and inside it are EditorProvider, editor DOM, and EditorCommand
      // Find the EditorContent div (one level up from editor's immediate parent)
      container = editorDom.parentElement?.parentElement as HTMLElement

      // Fallback: find by looking for EditorCommand's parent
      if (!container) {
        const editorCommandElement = document.querySelector('#slash-command')
        container = editorCommandElement?.parentElement as HTMLElement
      }

      // Final fallback: use editor's immediate parent
      if (!container) {
        container = editorDom.parentElement
      }

      if (!container) {
        return false
      }

      // Ensure container has positioning context for absolute positioning
      const containerStyle = window.getComputedStyle(container)
      if (containerStyle.position === 'static') {
        container.style.position = 'relative'
      }

      // Create popup element
      popup = document.createElement('div')
      popup.style.position = 'absolute'
      popup.style.zIndex = '1000'
      popup.style.width = 'max-content'
      popup.appendChild(component.element)

      // Append to the EditorContent container (same level as editor)
      container.appendChild(popup)

      // Verify clientRect is valid
      if (
        !clientRect ||
        isNaN(clientRect.left) ||
        isNaN(clientRect.top) ||
        isNaN(clientRect.width) ||
        isNaN(clientRect.height)
      ) {
        console.error('Invalid clientRect provided:', clientRect)
        return false
      }

      // Create virtual reference element from clientRect
      // Floating UI will use this to calculate position relative to container
      const virtualReference: ReferenceElement = {
        getBoundingClientRect: () => {
          // Get fresh clientRect if it's a function, otherwise use cached value
          const rect =
            typeof props.clientRect === 'function'
              ? props.clientRect()
              : clientRect
          // Return a valid DOMRect-like object
          return {
            width: rect.width || 1,
            height: rect.height || 1,
            top: rect.top,
            left: rect.left,
            bottom: rect.bottom,
            right: rect.right,
            x: rect.x ?? rect.left,
            y: rect.y ?? rect.top,
            toJSON: () => ({}),
          }
        },
      }

      // Position with Floating UI - it handles all coordinate conversion automatically
      const updatePosition = async () => {
        if (!popup || !container) {
          console.warn('Popup or container not available for positioning')
          return
        }

        // Ensure popup is in DOM and wait for it to have dimensions
        if (!popup.isConnected) {
          container.appendChild(popup)
        }

        // Wait for layout to calculate popup dimensions
        await new Promise((resolve) => requestAnimationFrame(resolve))

        const popupRect = popup.getBoundingClientRect()
        const virtualRect = virtualReference.getBoundingClientRect()

        console.log('Starting computePosition...', {
          popupDimensions: { width: popupRect.width, height: popupRect.height },
          virtualRect,
        })

        // If popup has no dimensions, wait a bit more
        if (popupRect.width === 0 && popupRect.height === 0) {
          console.warn('Popup has zero dimensions, waiting for layout...')
          await new Promise((resolve) => setTimeout(resolve, 50))
        }

        try {
          // Floating UI handles everything - just pass the strategy and it calculates relative to container
          const { x, y, strategy } = await computePosition(
            virtualReference,
            popup,
            {
              placement: 'bottom-start',
              strategy: 'absolute', // This makes Floating UI return coordinates relative to the offsetParent (our container)
              middleware: [shift({ padding: 8 }), flip()],
            },
          )

          console.log('computePosition result:', { x, y, strategy })

          // Validate results - NaN means Floating UI couldn't calculate
          if (isNaN(x) || isNaN(y)) {
            console.error('computePosition returned NaN!', {
              x,
              y,
              virtualRect,
              popupRect,
              container: container.getBoundingClientRect(),
            })
            return
          }

          // Apply the calculated position - Floating UI already did all the math
          if (popup) {
            popup.style.position = strategy
            popup.style.left = `${x}px`
            popup.style.top = `${y}px`

            console.log('Styles applied:', {
              position: popup.style.position,
              left: popup.style.left,
              top: popup.style.top,
            })
          }
        } catch (error) {
          console.error('Floating UI positioning error:', error)
        }
      }

      // Position after a short delay to ensure DOM layout is ready
      setTimeout(() => {
        updatePosition()
      }, 0)

      // Auto-update position
      cleanup = autoUpdate(virtualReference, popup, updatePosition)
    },
    onUpdate: (props: { editor: Editor; clientRect: () => DOMRect }) => {
      component?.updateProps(props)

      if (!props.clientRect || !popup) {
        return
      }

      // Update position with Floating UI - it handles everything automatically
      const virtualReference: ReferenceElement = {
        getBoundingClientRect: props.clientRect,
      }

      computePosition(virtualReference, popup, {
        placement: 'bottom-start',
        strategy: 'absolute',
        middleware: [shift({ padding: 8 }), flip()],
      })
        .then(({ x, y, strategy }) => {
          if (popup) {
            popup.style.position = strategy
            popup.style.left = `${x}px`
            popup.style.top = `${y}px`
          }
        })
        .catch((error) => {
          console.error('Floating UI positioning error on update:', error)
        })
    },

    onKeyDown: (props: { event: KeyboardEvent }) => {
      if (props.event.key === 'Escape') {
        if (popup) {
          popup.style.display = 'none'
        }
        return true
      }

      // @ts-ignore
      return component?.ref?.onKeyDown(props)
    },
    onExit: () => {
      if (cleanup) {
        cleanup()
        cleanup = null
      }
      if (popup && popup.parentNode) {
        popup.parentNode.removeChild(popup)
        popup = null
      }
      component?.destroy()
      container = null
    },
  }
}

export interface SuggestionItem {
  title: string
  description: string
  icon: ReactNode
  searchTerms?: string[]
  command?: (props: { editor: Editor; range: Range }) => void
}

export const createSuggestionItems = (items: SuggestionItem[]) => items

export const handleCommandNavigation = (event: KeyboardEvent) => {
  if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
    const slashCommand = document.querySelector('#slash-command')
    if (slashCommand) {
      return true
    }
  }
}

export { Command, renderItems }
